import { z } from 'zod';
import { eventSchema } from './envelope';

export const IDENTITY_ACCOUNT_CREATED_V1 = 'identity.account.created.v1' as const;
export const IDENTITY_ACCOUNT_ACTIVATED_V1 = 'identity.account.activated.v1' as const;
export const IDENTITY_INVITATION_SENT_V1 = 'identity.invitation.sent.v1' as const;
export const IDENTITY_INVITATION_ACCEPTED_V1 = 'identity.invitation.accepted.v1' as const;
export const IDENTITY_INVITATION_REVOKED_V1 = 'identity.invitation.revoked.v1' as const;
export const IDENTITY_INVITATION_CREATED_V1 = 'identity.invitation.created.v1' as const;
export const IDENTITY_INVITATION_RESENT_V1 = 'identity.invitation.resent.v1' as const;
export const IDENTITY_PASSWORD_RESET_V1 = 'identity.password.reset.v1' as const;
export const IDENTITY_SESSIONS_REVOKED_V1 = 'identity.sessions.revoked.v1' as const;
export const IDENTITY_LOGIN_FAILED_V1 = 'identity.login.failed.v1' as const;
export const IDENTITY_ACCOUNT_LOCKED_V1 = 'identity.account.locked.v1' as const;
export const IDENTITY_ACCOUNT_ACCESS_CHANGED_V1 = 'identity.account.access_changed.v1' as const;
export const IDENTITY_PASSWORD_RESET_SENT_V1 = 'identity.password.reset_sent.v1' as const;
export const IDENTITY_PLATFORM_ADMIN_CHANGED_V1 = 'identity.platform_admin.changed.v1' as const;
export const IDENTITY_MEMBER_INVITED_V1 = 'identity.member.invited.v1' as const;

/**
 * Een lid dat nog nooit toegang had, kreeg een uitnodiging (ADR-0101). Bij opnieuw versturen is
 * `resend` waar en werkt de vorige link niet meer. Zonder het adres: dat staat bij het account.
 */
export const memberInvitedV1Schema = eventSchema({
  invitationId: z.guid(),
  accountId: z.guid(),
  personId: z.guid(),
  expiresAt: z.string().datetime(),
  resend: z.boolean(),
});
export type MemberInvitedV1 = z.infer<typeof memberInvitedV1Schema>;

/** Master voegde een platformbeheerder toe, of (de)activeerde er een (MST-7). Zonder bedrijf. */
export const platformAdminChangedV1Schema = eventSchema({
  adminId: z.guid(),
  email: z.string(),
  action: z.enum(['created', 'disabled', 'enabled']),
});
export type PlatformAdminChangedV1 = z.infer<typeof platformAdminChangedV1Schema>;

/**
 * Master blokkeerde of deblokkeerde een account (MST-2). Geblokkeerd = status `disabled`, en de
 * sessies van dat account gaan dicht.
 */
export const accountAccessChangedV1Schema = eventSchema({
  accountId: z.guid(),
  action: z.enum(['blocked', 'unblocked']),
});

/** Master stuurde een account een mail om een nieuw wachtwoord te kiezen (MST-2). */
export const passwordResetSentV1Schema = eventSchema({
  accountId: z.guid(),
});

export type AccountAccessChangedV1 = z.infer<typeof accountAccessChangedV1Schema>;
export type PasswordResetSentV1 = z.infer<typeof passwordResetSentV1Schema>;

/**
 * Een account is aangemaakt. `directory` maakt hierop vanaf Stap 3 de persoon aan; tot
 * dan is `personId` een los uuid dat identity zelf toekent.
 */
export const accountCreatedV1Schema = eventSchema({
  accountId: z.guid(),
  personId: z.guid(),
  email: z.string().email().nullable(),
  displayName: z.string(),
});

export const accountActivatedV1Schema = eventSchema({
  accountId: z.guid(),
  personId: z.guid(),
});

export const invitationSentV1Schema = eventSchema({
  invitationId: z.guid(),
  email: z.string().email(),
  expiresAt: z.string().datetime(),
  /**
   * Of UP2D8 zelf uitnodigde, of het bedrijf (Extra 4).
   *
   * Voor het auditspoor (Stap 18): "wie liet deze beheerder binnen" is een andere vraag dan
   * "welk account deed het", en de eerste is degene die gesteld wordt.
   */
  invitedByKind: z.enum(['platform', 'tenant']).default('platform'),
  /**
   * De mail vertrekt nog niet mee, maar vanuit de worker na de commit (LOG-1): een uitnodiging
   * die ontstaat uit een event (nieuwe medewerker) heeft geen request om ze na af te handelen.
   */
  mailFromWorker: z.boolean().optional(),
});

/** Een uitnodiging is ingetrokken; het token werkt niet meer (Extra 4). */
export const invitationRevokedV1Schema = eventSchema({
  invitationId: z.guid(),
  /** Leeg bij een open uitnodiging (Stap 34): die heeft geen adres. */
  email: z.string().email().nullable(),
});

/**
 * Een uitnodiging voor medewerkers is aangemaakt (Stap 34): `person` of `open`.
 *
 * Zonder de code: die is een sleutel, en een event belandt in de outbox en later in een
 * auditspoor (Stap 18). `actor` in de envelop zegt wie ze maakte.
 */
export const invitationCreatedV1Schema = eventSchema({
  invitationId: z.guid(),
  kind: z.enum(['person', 'open']),
  maxUses: z.number().int().nullable(),
  expiresAt: z.string().datetime(),
});
export type InvitationCreatedV1 = z.infer<typeof invitationCreatedV1Schema>;

/** Een uitnodiging is opnieuw verstuurd met een nieuw token; het oude is ongeldig (Extra 4). */
export const invitationResentV1Schema = eventSchema({
  invitationId: z.guid(),
  email: z.string().email(),
  expiresAt: z.string().datetime(),
});

/**
 * `roleHint` zegt wat voor uitnodiging het was. `access` zet daarop de rol Beheerder;
 * het alternatief — identity een rol laten meegeven — zou identity laten weten welke
 * rollen er bestaan, en dat is niet zijn zaak.
 */
export const invitationAcceptedV1Schema = eventSchema({
  invitationId: z.guid(),
  accountId: z.guid(),
  personId: z.guid(),
  roleHint: z.enum(['admin', 'none']).default('none'),
  /**
   * De rol die bij de uitnodiging gekozen werd (Extra 4).
   *
   * Wint van `roleHint` als hij er is. `identity` kent geen rollen en geeft het id alleen door;
   * `access` beslist of die rol nog bestaat en kent hem toe - of valt terug op Beheerder.
   */
  roleId: z.guid().nullable().default(null),
});

export const passwordResetV1Schema = eventSchema({
  accountId: z.guid(),
  /** Hoe het wachtwoord gewijzigd is; relevant voor het auditspoor in Stap 18. */
  method: z.enum(['reset_link', 'self_service', 'invitation']),
});

/**
 * Een mislukte aanmelding. Bewust zonder het personeelsnummer of e-mailadres zelf: het
 * event gaat naar de outbox en straks naar het auditspoor (Stap 18), en daar hoort geen
 * lijst te ontstaan van namen die iemand geprobeerd heeft.
 */
export const loginFailedV1Schema = eventSchema({
  method: z.enum(['password', 'pin']),
  /** Bestond het account? Nodig om echte aanvallen van typefouten te onderscheiden. */
  accountExisted: z.boolean(),
  attempts: z.number().int(),
});

export const accountLockedV1Schema = eventSchema({
  accountId: z.guid(),
  personId: z.guid().nullable(),
  lockedUntil: z.string().datetime(),
  attempts: z.number().int(),
});

export const sessionsRevokedV1Schema = eventSchema({
  accountId: z.guid().nullable(),
  /**
   * Om wie het gaat, als het om één persoon gaat. Leeg wanneer een heel bedrijf wordt
   * opgeschort. Sinds Stap 9 nodig: `notifications` zet dan de pushabonnementen van die
   * persoon uit, en het mag geen account-id hoeven op te zoeken in een ander schema.
   */
  personId: z.guid().nullable().default(null),
  reason: z.string(),
  count: z.number().int(),
});

export type AccountCreatedV1 = z.infer<typeof accountCreatedV1Schema>;
export type AccountActivatedV1 = z.infer<typeof accountActivatedV1Schema>;
export type InvitationSentV1 = z.infer<typeof invitationSentV1Schema>;
export type InvitationAcceptedV1 = z.infer<typeof invitationAcceptedV1Schema>;
export type InvitationRevokedV1 = z.infer<typeof invitationRevokedV1Schema>;
export type InvitationResentV1 = z.infer<typeof invitationResentV1Schema>;
export type PasswordResetV1 = z.infer<typeof passwordResetV1Schema>;
export type SessionsRevokedV1 = z.infer<typeof sessionsRevokedV1Schema>;
export type LoginFailedV1 = z.infer<typeof loginFailedV1Schema>;
export type AccountLockedV1 = z.infer<typeof accountLockedV1Schema>;

export const identityEventCatalog = {
  [IDENTITY_ACCOUNT_ACCESS_CHANGED_V1]: accountAccessChangedV1Schema,
  [IDENTITY_PASSWORD_RESET_SENT_V1]: passwordResetSentV1Schema,
  [IDENTITY_MEMBER_INVITED_V1]: memberInvitedV1Schema,
  [IDENTITY_PLATFORM_ADMIN_CHANGED_V1]: platformAdminChangedV1Schema,
  [IDENTITY_INVITATION_CREATED_V1]: invitationCreatedV1Schema,
  [IDENTITY_ACCOUNT_CREATED_V1]: accountCreatedV1Schema,
  [IDENTITY_ACCOUNT_ACTIVATED_V1]: accountActivatedV1Schema,
  [IDENTITY_INVITATION_SENT_V1]: invitationSentV1Schema,
  [IDENTITY_INVITATION_ACCEPTED_V1]: invitationAcceptedV1Schema,
  [IDENTITY_INVITATION_REVOKED_V1]: invitationRevokedV1Schema,
  [IDENTITY_INVITATION_RESENT_V1]: invitationResentV1Schema,
  [IDENTITY_PASSWORD_RESET_V1]: passwordResetV1Schema,
  [IDENTITY_SESSIONS_REVOKED_V1]: sessionsRevokedV1Schema,
  [IDENTITY_LOGIN_FAILED_V1]: loginFailedV1Schema,
  [IDENTITY_ACCOUNT_LOCKED_V1]: accountLockedV1Schema,
} as const;

export const IDENTITY_KIOSK_CHANGED_V1 = 'identity.kiosk.changed.v1' as const;

/** Een kiosk is ingericht, gewijzigd of ingetrokken (KIO-1; voor het auditspoor, Stap 18). */
export const kioskChangedV1Schema = eventSchema({
  kioskId: z.guid(),
  name: z.string(),
  action: z.enum(['created', 'updated', 'revoked']),
});
export type KioskChangedV1 = z.infer<typeof kioskChangedV1Schema>;

// --- Account verwijderen (DEL-1, ADR-0098) ---------------------------------------------------

export const IDENTITY_ACCOUNT_DELETION_REQUESTED_V1 =
  'identity.account.deletion_requested.v1' as const;
export const IDENTITY_ACCOUNT_DELETION_CANCELLED_V1 =
  'identity.account.deletion_cancelled.v1' as const;
export const IDENTITY_ACCOUNT_ERASED_V1 = 'identity.account.erased.v1' as const;

/**
 * Iemand vroeg om zijn account te verwijderen; het account staat uit en wacht op wissen. Eén
 * event per account, met het bedrijf in de envelop. Geen naam of adres: dit event reist door het
 * auditspoor, en dat spoor blijft na het wissen bestaan.
 */
export const accountDeletionRequestedV1Schema = eventSchema({
  accountId: z.guid(),
  personId: z.guid(),
  origin: z.enum(['app', 'web']),
  scope: z.enum(['tenant', 'all']),
  eraseAfter: z.string().datetime(),
});
export type AccountDeletionRequestedV1 = z.infer<typeof accountDeletionRequestedV1Schema>;

/** Binnen de bedenktijd geannuleerd via de link in de mail; het account staat weer zoals ervoor. */
export const accountDeletionCancelledV1Schema = eventSchema({
  accountId: z.guid(),
  personId: z.guid(),
});
export type AccountDeletionCancelledV1 = z.infer<typeof accountDeletionCancelledV1Schema>;

/**
 * De bedenktijd is voorbij en het account is gewist. Wie gegevens over deze persoon bewaart, wist
 * of pseudonimiseert ze nu: `directory` het profiel, `notifications` de toestellen en de inbox.
 */
export const accountErasedV1Schema = eventSchema({
  accountId: z.guid(),
  personId: z.guid(),
});
export type AccountErasedV1 = z.infer<typeof accountErasedV1Schema>;
